<template>
  <div class="auditoria-page">
    <DataTable
      title="Inscrições"
      subtitle="Listagem e exportação (inclui registros excluídos)"
      title-icon="fas fa-file-alt"
      :data="items"
      :columns="columns"
      :loading="loading"
      :custom-filters="filters"
      search-placeholder="Buscar por nome, CPF ou e-mail..."
      empty-title="Nenhuma inscrição encontrada"
      empty-message="Não há inscrições para os concursos permitidos."
      empty-icon="fas fa-file-alt"
      :show-view="false"
      :show-edit="false"
      :show-delete="false"
      @filter="onFilter"
    >
      <template #header-actions>
        <button class="btn btn-success" @click="exportar" :disabled="exporting">
          <i :class="exporting ? 'fas fa-spinner fa-spin' : 'fas fa-download'"></i>
          {{ exporting ? 'Exportando...' : 'Exportar' }}
        </button>
      </template>

      <template #cell-deleted_at="{ value }">
        <span v-if="value" class="badge badge-danger">Excluído</span>
        <span v-else class="badge badge-success">Ativo</span>
      </template>

      <template #cell-status="{ value }">
        <span class="badge" :class="getStatusBadgeClass(value)">
          {{ getStatusLabel(value) }}
        </span>
      </template>
    </DataTable>
  </div>
</template>

<script>
import axios from 'axios'
import DataTable from '@/components/admin/DataTable.vue'

export default {
  name: 'AuditoriaInscricoes',
  components: { DataTable },
  data() {
    return {
      loading: false,
      exporting: false,
      items: [],
      concursos: [],
      tableFilters: { search: '', concurso_id: '', status: '' },
      columns: [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'concurso.titulo', label: 'Concurso', sortable: true },
        { key: 'vaga.cargo', label: 'Cargo', sortable: true },
        { key: 'cadastro.nome', label: 'Candidato', sortable: true },
        { key: 'cadastro.cpf', label: 'CPF', sortable: true },
        { key: 'cadastro.email', label: 'E-mail', sortable: true },
        { key: 'status', label: 'Status', sortable: true, type: 'badge' },
        { key: 'created_at', label: 'Criado em', sortable: true, type: 'date' },
        { key: 'deleted_at', label: 'Registro', sortable: true }
      ],
      filters: [
        { key: 'concurso_id', label: 'Concurso', placeholder: 'Todos os concursos', options: [] },
        {
          key: 'status',
          label: 'Status',
          placeholder: 'Todos os status',
          options: [
            { value: 'A', label: 'Ativa' },
            { value: 'C', label: 'Cancelada' },
            { value: 'D', label: 'Deferida' },
            { value: 'I', label: 'Indeferida' },
            { value: 'P', label: 'Pendente' }
          ]
        }
      ]
    }
  },
  async mounted() {
    await Promise.all([this.carregarConcursos(), this.carregar()])
  },
  methods: {
    async carregarConcursos() {
      try {
        const { data } = await axios.get('/ps/auditoria/concursos')
        this.concursos = Array.isArray(data) ? data : []
        this.filters[0].options = this.concursos.map(c => ({
          value: c.id,
          label: `${c.prefeitura} - ${c.titulo}`
        }))
      } catch (e) {
        console.error('Erro ao carregar concursos auditoria (filtros):', e)
      }
    },
    async carregar(params = {}) {
      this.loading = true
      try {
        const { data } = await axios.get('/ps/auditoria/inscricoes', { params })
        const items = Array.isArray(data) ? data : []
        // Ordenar por inscrição (id) para agrupar, e depois por data de criação (desc)
        this.items = items.sort((a, b) => {
          const aId = a?.id ?? 0
          const bId = b?.id ?? 0
          if (aId !== bId) return aId - bId
          const aDate = new Date(a?.created_at || 0).getTime()
          const bDate = new Date(b?.created_at || 0).getTime()
          return bDate - aDate
        })
      } catch (e) {
        console.error('Erro ao carregar inscrições auditoria:', e)
        this.$sweet.showError('Erro ao carregar inscrições')
      } finally {
        this.loading = false
      }
    },
    onFilter(filters) {
      this.tableFilters = filters || { search: '', concurso_id: '', status: '' }
      // Mantemos simples: não recarrega listagem no servidor.
    },
    async exportar() {
      this.exporting = true
      try {
        this.$sweet.showInfo('Gerando arquivo Excel...')
        const response = await axios.get('/ps/auditoria/inscricoes/exportar-excel', {
          params: this.tableFilters,
          responseType: 'blob'
        })

        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `auditoria_inscricoes_${new Date().getTime()}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        this.$sweet.showSuccess('Arquivo exportado com sucesso!')
      } catch (e) {
        console.error('Erro ao exportar inscrições auditoria:', e)
        this.$sweet.showError('Erro ao exportar dados')
      } finally {
        this.exporting = false
      }
    },
    getStatusLabel(status) {
      const map = {
        A: 'Ativa',
        C: 'Cancelada',
        D: 'Deferida',
        I: 'Indeferida',
        P: 'Pendente'
      }
      return map[status] || (status || '-')
    },
    getStatusBadgeClass(status) {
      if (status === 'D') return 'badge-success'
      if (status === 'I' || status === 'C') return 'badge-danger'
      if (status === 'P') return 'badge-warning'
      return 'badge-secondary'
    }
  }
}
</script>

<style lang="scss" scoped>
.auditoria-page {
  .badge {
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
  }
  .badge-success {
    background: rgba(40, 167, 69, 0.1);
    color: #28a745;
  }
  .badge-danger {
    background: rgba(220, 53, 69, 0.1);
    color: #dc3545;
  }
  .badge-warning {
    background: rgba(255, 193, 7, 0.1);
    color: #ffc107;
  }
  .badge-secondary {
    background: rgba(108, 117, 125, 0.1);
    color: #6c757d;
  }
}
</style>

